import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { of } from 'rxjs';
import { catchError, debounceTime, filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { ArticleKind, INewspaper } from '../../models/article.model';
import { NewspaperApiService } from '../../services/api/newspaper.service';
import { IErrorResult } from '../../services/dto.service';
import { GoogleService } from '../../services/utility/google.service';
import { AppState, INewspaperState } from '../app.state';
import { fromCertificateActions } from '../certificate/certificate.actions';
import { CertificateSelector } from '../certificate/certificate.selectors';
import { EntityEffects } from '../entity.effects';
import { fromMagazineActions } from '../magazine/magazine.actions';
import { RegionSelector } from '../region/region.selectors';
import { RouterSelector } from '../router/router.selectors';
import { fromNewspaperActions } from './newspaper.actions';
import { NewspaperSelector } from './newspaper.selectors';

@Injectable()
export class NewspaperEffects extends EntityEffects<INewspaper, INewspaperState> {
	constructor(
		actions$: Actions,
		store: Store<AppState>,
		routerSelector: RouterSelector,
		private newspaperApiService: NewspaperApiService,
		private newspaperSelector: NewspaperSelector,
		private regionSelector: RegionSelector,
		private certificateSelector: CertificateSelector,
		private googleService: GoogleService
	) {
		super(actions$, store, newspaperApiService, newspaperSelector, routerSelector, fromNewspaperActions);
	}

	public onLoad$: any = createEffect(() =>
		this.actions$.pipe(
			ofType(this.entityActions.load),
			debounceTime(10),
			concatLatestFrom(() => this.store.select(this.newspaperSelector.date)),
			concatLatestFrom(() => this.store.select(this.regionSelector.selected)),
			filter(([[, date]]) => date != null),
			switchMap(([[, date], region]) =>
				this.newspaperApiService.search({ date: moment.utc(date).toDate(), region, dateFrom: moment.utc(date).toDate(), dateUntil: moment.utc(date).toDate(), articleKind: ArticleKind.Newspaper }, { skip: 0, top: 500 }).pipe(
					map(data => this.entityActions.loaded({ list: data.map(rating => rating.article), ratings: data.map(rating => ({ score: rating.score, isPerfectMatch: rating.isPerfectMatch, articleId: rating.article._id })) })),
					catchError((response: IErrorResult) => of(this.entityActions.failed({ message: response.error?.error || response.error?.message })))
				)
			)
		)
	);

	public onLoadedForAnalysis$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(this.entityActions.loaded),
				concatLatestFrom(() => this.store.select(this.newspaperSelector.paging)),
				filter(([{ list }, paging]) => list.length == paging.top),
				mergeMap(([{ list }]) => [this.googleService.viewItemList(list)])
			),
		{ dispatch: false }
	);

	public onDayRangeChanged$: any = createEffect(() =>
		this.actions$.pipe(
			ofType(fromNewspaperActions.dayRangeChanged, fromNewspaperActions.pagingChanged),
			concatLatestFrom(() => this.store.select(this.newspaperSelector.list)),
			concatLatestFrom(() => this.store.select(this.newspaperSelector.isLoading)),
			concatLatestFrom(() => this.store.select(this.newspaperSelector.date)),
			concatLatestFrom(() => this.store.select(this.newspaperSelector.paging)),
			concatLatestFrom(() => this.store.select(this.newspaperSelector.dayRange)),
			concatLatestFrom(() => this.store.select(this.regionSelector.selected)),
			filter(([[[[[], date]]]]) => date != null),
			switchMap(([[[[[[, list], isLoading], date], paging], dayRange], region]) => {
				if (isLoading || list.length >= paging.top || dayRange.current > dayRange.max) {
					return of(fromNewspaperActions.extended());
				}

				return this.newspaperApiService
					.search({ date: moment.utc(date).toDate(), region, dateFrom: moment.utc(date).toDate(), dateUntil: moment.utc(date).add(dayRange.current, 'days').toDate(), articleKind: ArticleKind.Newspaper }, paging)
					.pipe(
						map(data => this.entityActions.loaded({ list: data.map(rating => rating.article), ratings: data.map(rating => ({ score: rating.score, isPerfectMatch: rating.isPerfectMatch, articleId: rating.article._id })) })),
						catchError((response: IErrorResult) => of(this.entityActions.failed({ message: response.error?.error || response.error?.message })))
					);
			})
		)
	);

	public onLoadedForDayRange$ = createEffect(() =>
		this.actions$.pipe(
			ofType(this.entityActions.loaded),
			concatLatestFrom(() => this.store.select(this.newspaperSelector.date)),
			filter(([{ list }, date]) => list.find(item => moment(item.publicationDate).isSame(date, 'day')) == null),
			concatLatestFrom(() => this.store.select(this.newspaperSelector.dayRange)),
			map(([, dayRange]) => fromNewspaperActions.dayRangeChanged({ dayRange: { ...dayRange, current: dayRange.current * 2 } }))
		)
	);

	public onDateChangedForSearch$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromNewspaperActions.dateChanged),
			filter(({ shouldTriggerSearch }) => shouldTriggerSearch),
			map(() => fromNewspaperActions.load())
		)
	);

	public onLoadedForMagazines$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromNewspaperActions.loaded),
			map(() => fromMagazineActions.load())
		)
	);

	public onSelected$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromNewspaperActions.selected),
			concatLatestFrom(() => this.store.select(this.newspaperSelector.date)),
			filter(([{ entity }]) => entity != null),
			map(([{ entity }, date]) => fromNewspaperActions.dateChanged({ date: date || entity.publicationDate, shouldTriggerSearch: true }))
		)
	);

	public onDateChanged$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromNewspaperActions.dateChanged),
			concatLatestFrom(() => this.store.select(this.newspaperSelector.date)),
			concatLatestFrom(() => this.store.select(this.certificateSelector.selected)),
			filter(([[, date], certificate]) => date != null && certificate != null),
			map(([[, date]]) => fromCertificateActions.dateChanged({ date }))
		)
	);

	public onDateChangedForAnalysis$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromNewspaperActions.dateChanged),
				concatLatestFrom(() => this.store.select(this.newspaperSelector.date)),
				filter(([{ date }, currentDate]) => !moment(date).isSame(currentDate, 'day')),
				mergeMap(([{ date }]) => [this.googleService.changeDate(date)])
			),
		{ dispatch: false }
	);

	public onNavigated$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ROUTER_NAVIGATED),
			concatLatestFrom(() => this.store.select(this.routerSelector.newspaperId)),
			filter(([, newspaperId]) => newspaperId != null),
			map(([, newspaperId]) => fromNewspaperActions.loadNewspaper({ id: newspaperId }))
		)
	);

	public onNavigatedForSearchDate$ = createEffect(() =>
		this.actions$.pipe(
			ofType(ROUTER_NAVIGATED),
			concatLatestFrom(() => this.store.select(this.routerSelector.searchDate)),
			filter(([, searchDate]) => !!searchDate),
			map(([, searchDate]) => searchDate.replace(/[^a-zA-Z0-9]/g, ' ')),
			map(searchDate => require('any-date-parser').attempt(searchDate, 'de-DE')),
			filter(result => result.invalid == null),
			map(result => new Date(Date.UTC(result.year, result.month - 1, result.day, 0, 0, 0, 0))),
			map(date => fromNewspaperActions.dateChanged({ date, shouldTriggerSearch: true }))
		)
	);

	public onLoadNewspaper$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromNewspaperActions.loadNewspaper),
			switchMap(({ id }) => this.newspaperApiService.getMany([id]).pipe(map(newspapers => fromNewspaperActions.loadedNewspaper({ newspaper: newspapers[0] }))))
		)
	);

	public onLoadedNewspaperForAnalysis$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromNewspaperActions.loadedNewspaper),
				mergeMap(({ newspaper }) => [this.googleService.viewItem(newspaper)])
			),
		{ dispatch: false }
	);

	public onNewspaperSelectedForAnalysis$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromNewspaperActions.selected),
				filter(({ entity }) => entity != null),
				debounceTime(100),
				mergeMap(({ entity }) => [this.googleService.selectNewspaper(entity)])
			),
		{ dispatch: false }
	);
}
